// features/onboarding/InterestPicker.tsx — the shared "topics you're into" picker.
//
// Used by BOTH the first-run flow (step 1) and Settings → Content ("Interests"),
// so a member can revisit the choice later — the same control, the same write
// (`updateProfile({ interests })`, done by the container).
//
// It writes the POSITIVE list. This is deliberately NOT ForYouTopicMenu
// (features/home/FeedTabs.tsx), which edits `feedTopics` — the opt-OUT list.
// Piping picks into that field would HIDE every topic the member just chose.
//
// Accessibility (hand-rolled, no <input>): a labelled group of buttons with
// role="checkbox" + aria-checked, keyboard-reachable in DOM order; Space/Enter
// toggle via the native button click. Icons are SVG only, never emoji.

import styled from 'styled-components';
import { Icon } from '@/icons';
import type { TopicOption } from '@/core/models/account';

const Group = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Pill = styled.button<{ $on: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  background: ${({ theme, $on }) =>
    $on ? `color-mix(in srgb, ${theme.color.accentCyan} 16%, transparent)` : 'transparent'};
  color: ${({ theme, $on }) => ($on ? theme.color.textPrimary : theme.color.textMuted)};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ $on }) => ($on ? 600 : 500)};
  cursor: pointer;
  transition: filter 120ms ease, background 120ms ease, color 120ms ease;
  &:hover:not(:disabled) {
    filter: brightness(1.12);
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

const Tick = styled.span`
  display: inline-flex;
  line-height: 0;
  color: ${({ theme }) => theme.color.accentCyan};
`;

export interface InterestPickerProps {
  /** The 8 topic options (from GET /users/topics/, or the client fallback). */
  topics: TopicOption[];
  /** Selected topic keys. */
  value: string[];
  onChange: (next: string[]) => void;
  /** Accessible name of the group. */
  label?: string;
  disabled?: boolean;
}

export function InterestPicker({
  topics,
  value,
  onChange,
  label = 'Topics you are into',
  disabled = false,
}: InterestPickerProps) {
  const selected = new Set(value);

  function toggle(key: string) {
    if (selected.has(key)) {
      onChange(value.filter((k) => k !== key));
    } else {
      // Preserve taxonomy order rather than click order — the server compares
      // sets, but a stable order keeps optimistic-vs-server diffs quiet.
      const next = new Set(value);
      next.add(key);
      onChange(topics.map((t) => t.key).filter((k) => next.has(k)));
    }
  }

  return (
    <Group role="group" aria-label={label}>
      {topics.map((t) => {
        const on = selected.has(t.key);
        return (
          <Pill
            key={t.key}
            type="button"
            role="checkbox"
            aria-checked={on}
            $on={on}
            disabled={disabled}
            onClick={() => toggle(t.key)}
          >
            {on && (
              <Tick aria-hidden="true">
                <Icon name="check" size={14} />
              </Tick>
            )}
            {t.label}
          </Pill>
        );
      })}
    </Group>
  );
}
